import { useUser } from "@/libs/hooks/use-user";
import Loader from "@quest/ui/components/loader";
import { useNavigate } from "react-router-dom";
import { ReactNode, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@quest/hooks";
import { setUserDetails } from "@quest/slice/auth/auth-slice";
import { ROLES } from "../constants/roles";
import { logout } from "../utils/logout";
import Cookies from "js-cookie";
import { AlertCircle } from "lucide-react";

interface AuthGuardProps {
  children: ReactNode;
  requiredRoles?: string[];
  redirectTo?: string;
}

function isStaffViewAsPreview(
  role: string | undefined,
  previewActive: boolean
): boolean {
  return (
    previewActive &&
    (role === ROLES.TENANT_ADMIN || role === ROLES.TEACHER)
  );
}

export function AuthGuard({
  children,
  requiredRoles = [],
  redirectTo,
}: AuthGuardProps) {
  const { data: user, isLoading, isError, error } = useUser();
  const previewActive = useAppSelector((state) =>
    Boolean(state.preview?.active)
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (user) {
      dispatch(
        setUserDetails({
          id: user?.data?.id,
          email: user?.data?.email,
          firstName: user?.data?.firstName,
          lastName: user?.data?.lastName,
          role: user?.data?.role,
          idpId: user?.data?.idpId,
          studentId: user?.data?.id?.toString() || "",
          has_skipped_tutorial: user?.data?.has_skipped_tutorial || false,
          profile_image: user?.data?.profile_image || null,
          selected_education: user?.data?.selected_education || null,
          selected_year: user?.data?.selected_year || null,
          selected_occupation: user?.data?.selected_occupation || null,
          selected_profile_choice: user?.data?.selected_profile_choice || null,
          selected_type: user?.data?.selected_type || null,
        })
      );
    }
  }, [user]);

  const navigate = useNavigate();

  const hasRequiredRole = (role: string | undefined) => {
    if (requiredRoles.length === 0) return true;
    if (requiredRoles.some((requiredRole) => role === requiredRole)) {
      return true;
    }
    if (
      requiredRoles.includes(ROLES.STUDENT) &&
      isStaffViewAsPreview(role, previewActive)
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isLoading && user) {
      if (requiredRoles.length > 0 && !hasRequiredRole(user?.data?.role)) {
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
        }
      }
    }
  }, [user, isLoading, requiredRoles, redirectTo, previewActive]);

  if (user?.data?.role === ROLES.SUPER_ADMIN) {
    logout();
  }

  const hasToken = !!Cookies.get("accessToken");

  useEffect(() => {
    if (!hasToken && !isLoading) {
      Cookies.set("from", window.location.pathname);
      navigate("/login", { replace: true });
    }
  }, [hasToken, isLoading]);

  if (isLoading && !user) {
    return <Loader />;
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <h1 className="text-xl font-semibold text-gray-900">
              {error?.message || "Something went wrong"}
            </h1>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRoles.length > 0 && !hasRequiredRole(user?.data?.role)) {
    return null;
  }

  return <>{user && children}</>;
}
